import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { GridFour, FloppyDisk, CursorClick, ClockCounterClockwise, Printer, SpeakerHigh } from "@phosphor-icons/react";
import { usePillars, pillarChipStyle } from "@/contexts/PillarsContext";
import { useAuth } from "@/contexts/AuthContext";
import { roleLabel } from "@/lib/roles";

const GRADES = ["G7", "G8", "G9", "G10", "G11", "G12", "DEMO"];
const gLabel = (g) => (g === "DEMO" ? "Demo" : g);

export const fmtDur = (mins) => {
  const m = Math.round(mins || 0);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}`;
};
const INTRO_KEY = "__intro__";

export default function GradeMatrixPanel() {
  const { user } = useAuth();
  const isSA = user?.role === "super_admin";
  const [courses, setCourses] = useState(null);
  const [audit, setAudit] = useState(null);
  const [mtab, setMtab] = useState("school");
  const [err, setErr] = useState("");
  const [ts, setTs] = useState(null);
  const [tsBusy, setTsBusy] = useState(false);

  const loadTs = () => api.get("/admin/coach/transcribe-status").then(({ data }) => setTs(data)).catch(() => {});
  useEffect(() => { if (isSA) loadTs(); }, [isSA]);
  useEffect(() => {
    if (!ts?.state?.running) return;
    const t = setTimeout(loadTs, 4000);
    return () => clearTimeout(t);
  }, [ts]);

  const startTranscribeAll = async () => {
    setTsBusy(true);
    try {
      await api.post("/admin/coach/transcribe-videos");
      toast.success("Transcription started — new and changed lesson videos are being transcribed");
      setTimeout(loadTs, 1500);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTsBusy(false); }
  };

  // French voice-track (Voix FR) build progress
  const [vp, setVp] = useState(null);
  const [vpBusy, setVpBusy] = useState(false);
  const loadVp = () => api.get("/admin/voice-pregen/status").then(({ data }) => setVp(data)).catch(() => {});
  useEffect(() => { if (isSA) loadVp(); }, [isSA]);
  useEffect(() => {
    if (!vp?.running) return;
    const t = setTimeout(loadVp, 8000);
    return () => clearTimeout(t);
  }, [vp]);
  const startVoiceBuild = async () => {
    setVpBusy(true);
    try {
      const { data } = await api.post("/admin/voice-pregen/run");
      toast.success(data.already_running ? "Already building — progress updates below" : "French voice build started");
      setTimeout(loadVp, 1500);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setVpBusy(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/grade-matrix/courses");
        setCourses(data);
      } catch (e) { setErr(formatApiError(e)); }
      if (isSA) {
        try { const { data } = await api.get("/admin/matrix-audit"); setAudit(data); } catch {}
      }
    })();
  }, [isSA]);

  if (err) return <div className="text-[#FF5100] text-sm" data-testid="matrix-error">{err}</div>;
  if (!courses) return <div className="text-[#000000] text-sm">Loading matrix…</div>;

  return (
    <div className="flex flex-col gap-6" data-testid="grade-matrix-panel">
      <div className="flex gap-2 items-center justify-between flex-wrap">
        <div className="flex gap-2">
          {[["school", "School Matrix"], ...(isSA ? [["public", "Public (Adults)"]] : [])].map(([id, label]) => (
            <button key={id} type="button" data-testid={`matrix-tab-${id}`}
              className="px-5 py-2.5 rounded-xl font-display font-bold text-sm cursor-pointer transition-all"
              style={{
                background: mtab === id ? "#0A0D14" : "#FFFFFF",
                color: mtab === id ? "#FFFFFF" : "#52525B",
                border: "2px solid " + (mtab === id ? "#0A0D14" : "#C4C8CE"),
              }}
              onClick={() => setMtab(id)}>
              {label}
            </button>
          ))}
        </div>
        {isSA && (
          <div className="flex items-center gap-2">
            {ts && (
              <span className="text-xs text-[#52525B]" data-testid="matrix-transcribe-progress">
                {ts.state?.running
                  ? `Transcribing… ${ts.state.done || 0}/${ts.state.total || 0}`
                  : `${ts.transcribed}/${ts.lessons_with_video} lessons transcribed`}
              </span>
            )}
            <button type="button" className="btn-secondary text-xs" onClick={startTranscribeAll}
              disabled={tsBusy || ts?.state?.running} data-testid="matrix-transcribe-all-btn"
              title="Transcribe every lesson video that is new or has changed (shown under each video; used by Coach and search)">
              {ts?.state?.running ? "Transcribing…" : "Transcribe all"}
            </button>
          </div>
        )}
      </div>
      {isSA && vp && vp.total_videos > 0 && (
        <div className="card" data-testid="voice-pregen-card" style={{ padding: "14px 18px" }}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <SpeakerHigh size={18} color="#00B8D4" weight="fill" />
              <span className="font-display font-bold text-sm">French voice tracks (Voix FR)</span>
              <span className="text-xs text-[#52525B]" data-testid="voice-pregen-count">
                {vp.ready_tracks}/{vp.total_videos} videos ready
                {vp.running ? ` — building… (${vp.done || 0} built, ${vp.failed || 0} failed this run)` : ""}
              </span>
            </div>
            <button type="button" className="btn-secondary text-xs" onClick={startVoiceBuild}
              disabled={vpBusy || vp.running} data-testid="voice-pregen-run-btn"
              title="Build the French voice-over + subtitles for every video that doesn't have one yet (also runs automatically every night at 2am)">
              {vp.running ? "Building…" : vp.ready_tracks >= vp.total_videos ? "Rebuild missing" : "Generate missing now"}
            </button>
          </div>
          <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: "#ECEDEF" }} data-testid="voice-pregen-bar">
            <div className="h-full rounded-full transition-all" style={{
              width: `${Math.min(100, Math.round((vp.ready_tracks / vp.total_videos) * 100))}%`,
              background: vp.running ? "#00B8D4" : "#9EC500",
            }} />
          </div>
          <p className="text-[11px] text-[#52525B] mt-1.5 mb-0">
            Each track is the lesson's French subtitles spoken over the muted video. New uploads are picked up by the
            nightly 2am build automatically{vp.failed ? ` — ${vp.failed} failed this run and will be retried tonight` : ""}.
          </p>
        </div>
      )}
      {mtab === "school" ? (
        <p className="text-sm text-[#000000]">
          Pick a grade, then click lessons to turn them on/off for that grade — lime outline = on, greyed = off.
          Lessons with a 🔒 are locked to public-only and can't be enabled for any grade here — unlock them in the
          Public (Adults) tab. Lesson content itself can't be edited here.
        </p>
      ) : (
        <p className="text-sm text-[#000000]">
          Public (adult) learners always see every lesson in the course — grades don't apply to them.
          Locking a lesson here hides it from <b>every school grade</b> and greys it out in the School Matrix:
          no reset, bulk toggle, or builder save can re-enable it for schools until you unlock it here.
        </p>
      )}
      {courses.map(c => <MatrixCourse key={c.id} course={c} mtab={mtab} />)}
      {isSA && mtab === "school" && <MatrixAudit entries={audit} />}
    </div>
  );
}

function MatrixCourse({ course, mtab }) {
  const { pillars, colorOf, labelOf, introLabel } = usePillars();
  const [grid, setGrid] = useState(() => {
    const g = {};
    course.lessons.forEach(l => { g[l.lesson_asset_id] = new Set(l.grades); });
    return g;
  });
  const [locks, setLocks] = useState(() => {
    const m = {};
    course.lessons.forEach(l => { m[l.lesson_asset_id] = !!l.public_only; });
    return m;
  });
  const [editGrade, setEditGrade] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const { data } = await api.get(`/admin/courses/${course.id}/grade-matrix/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Grade-Matrix-Snapshot-${course.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Snapshot PDF downloaded — ready to print for the board");
    } catch (e) { toast.error("Couldn't create the snapshot PDF"); }
    finally { setExporting(false); }
  };

  const ExportBtn = (
    <button
      type="button"
      data-testid={`export-matrix-pdf-${course.id}`}
      onClick={exportPdf}
      disabled={exporting}
      className="flex items-center gap-1.5 text-sm font-display font-bold px-4 py-2 rounded-full cursor-pointer transition-all hover:brightness-110 disabled:opacity-50 whitespace-nowrap"
      style={{ background: "#0A0D14", color: "#CCFF00", border: "2px solid #0A0D14", boxShadow: "0 2px 8px rgba(10,13,20,0.25)" }}
      title="Download a printable PDF grid of which grades see which lessons — perfect for board meetings">
      <Printer size={16} weight="bold" /> {exporting ? "Preparing…" : "Board Snapshot (PDF)"}
    </button>
  );

  const toggle = (aid, grade) => {
    if (locks[aid]) return;
    setGrid(prev => {
      const next = { ...prev };
      const s = new Set(next[aid]);
      s.has(grade) ? s.delete(grade) : s.add(grade);
      next[aid] = s;
      return next;
    });
    setDirty(true);
  };

  const bulk = (grade, on) => {
    setGrid(prev => {
      const next = {};
      course.lessons.forEach(l => {
        const s = new Set(prev[l.lesson_asset_id]);
        if (!locks[l.lesson_asset_id]) { on ? s.add(grade) : s.delete(grade); }
        next[l.lesson_asset_id] = s;
      });
      return next;
    });
    setDirty(true);
  };

  const setLock = async (aid, on) => {
    try {
      await api.put(`/admin/courses/${course.id}/lessons/${aid}/public-only`, { public_only: on });
      setLocks(prev => ({ ...prev, [aid]: on }));
      if (on) setGrid(prev => ({ ...prev, [aid]: new Set() }));
      toast.success(on ? "Locked — hidden from all school grades" : "Unlocked — you can now set grades in the School Matrix");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const matrix = {};
      Object.entries(grid).forEach(([aid, set]) => { matrix[aid] = [...set]; });
      await api.put(`/admin/courses/${course.id}/grade-matrix`, { matrix });
      setDirty(false);
      toast.success("Matrix saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Columns: intro (no pillar) first, then pillars in catalog order — only ones with lessons.
  const groups = {};
  course.lessons.forEach(l => {
    const key = l.pillar || INTRO_KEY;
    (groups[key] = groups[key] || []).push(l);
  });
  const columns = [
    ...(groups[INTRO_KEY] ? [{ key: INTRO_KEY, name: introLabel, color: "#52525B" }] : []),
    ...pillars.filter(p => groups[p.slug]).map(p => ({ key: p.slug, name: p.name, color: p.color })),
  ];
  const maxRows = Math.max(0, ...columns.map(col => groups[col.key].length));

  if (mtab === "public") {
    return (
      <div className="rounded-2xl border border-[#C4C8CE] bg-[#DCDEE2] overflow-hidden" data-testid={`public-matrix-${course.id}`}>
        <div className="flex items-center px-4 py-3 border-b border-[#C4C8CE] font-display font-bold gap-2">
          <GridFour size={16} weight="duotone" /> {course.title}
          <span className="text-xs text-[#000000] font-sans font-normal mr-auto" data-testid={`public-course-duration-${course.id}`}>
            · {course.lessons.filter(l => locks[l.lesson_asset_id]).length} locked public-only · ~{fmtDur(course.lessons.reduce((s, l) => s + (l.duration_minutes || 0), 0))} total
          </span>
          {ExportBtn}
        </div>
        <div className="overflow-x-auto p-2">
          <table className="w-full border-collapse" data-testid={`public-matrix-table-${course.id}`}>
            <thead>
              <tr>
                <th className="w-9 p-2 text-[10px] uppercase tracking-[0.12em] text-[#000000]">#</th>
                {columns.map(col => (
                  <th key={col.key} className="text-left p-2 min-w-[164px]">
                    <span className="chip text-[10px] font-bold" style={pillarChipStyle(col.color)}>{col.name}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxRows }).map((_, r) => (
                <tr key={r} className="border-t border-[#E4E4E7]">
                  <td className="p-2 align-middle text-center font-display font-bold text-[#000000]">{r + 1}</td>
                  {columns.map(col => {
                    const l = groups[col.key][r];
                    return (
                      <td key={col.key} className="p-2 align-top">
                        {l && (
                          <PublicLessonCard
                            lesson={l}
                            locked={!!locks[l.lesson_asset_id]}
                            onLock={setLock}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#C4C8CE] bg-[#DCDEE2] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#C4C8CE]">
        <div className="font-display font-bold flex items-center gap-2">
          <GridFour size={16} weight="duotone" /> {course.title}
          <span className="text-xs text-[#000000] font-sans font-normal" data-testid={`school-course-duration-${course.id}`}>
            · {course.lessons.length} lessons · ~{fmtDur(course.lessons.reduce((s, l) => s + (l.duration_minutes || 0), 0))} total
          </span>
        </div>
        <div className="flex items-center gap-2">
          {ExportBtn}
          <button
            data-testid={`save-grade-matrix-${course.id}`}
            className="btn-primary text-sm flex items-center gap-1 disabled:opacity-40"
            onClick={save} disabled={!dirty || saving}>
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : (dirty ? "Save matrix" : "Saved")}
          </button>
        </div>
      </div>

      {/* Live per-grade time totals (reflect unsaved toggles) */}
      <div className="flex items-center gap-2 flex-wrap text-xs px-4 py-2 border-b border-[#C4C8CE]" data-testid={`grade-durations-${course.id}`}>
        <span className="font-bold uppercase tracking-[0.1em] text-[#000000]">Est. time by grade</span>
        {GRADES.map(g => (
          <span key={g} className="px-2 py-0.5 rounded-full bg-white border border-[#C4C8CE] font-display">
            <b>{g === "DEMO" ? "Demo" : g.replace("G", "Gr ")}</b>&nbsp;
            {fmtDur(course.lessons.reduce((s, l) =>
              s + ((!locks[l.lesson_asset_id] && grid[l.lesson_asset_id]?.has(g)) ? (l.duration_minutes || 0) : 0), 0))}
          </span>
        ))}
      </div>

      {/* Quick-select: pick a grade, then click lessons to toggle it on/off */}
      <div className="flex items-center gap-2 flex-wrap text-xs px-4 py-2 border-b"
        style={{ background: editGrade ? "#F7FEE7" : "transparent", borderColor: editGrade ? "#BEF264" : "#C4C8CE" }}
        data-testid={`grade-quickselect-${course.id}`}>
        <span className="text-[#3F6212] font-bold uppercase tracking-[0.1em] flex items-center gap-1">
          <CursorClick size={14} weight="bold" /> Quick-select grade
        </span>
        <button data-testid={`edit-grade-off-${course.id}`} onClick={() => setEditGrade("")}
          className="px-2 py-0.5 rounded-full font-display font-bold border transition-colors"
          style={{ background: editGrade === "" ? "#0A0D14" : "#FFFFFF", color: editGrade === "" ? "#FFFFFF" : "#52525B", borderColor: "#C4C8CE" }}>
          Off
        </button>
        {GRADES.map(g => {
          const on = editGrade === g;
          return (
            <button key={g} data-testid={`edit-grade-${course.id}-${g}`} onClick={() => setEditGrade(on ? "" : g)}
              className="px-2.5 py-0.5 rounded-full font-display font-bold border transition-colors"
              style={{ background: on ? "#CCFF00" : "#FFFFFF", color: on ? "#1F2937" : "#52525B", borderColor: on ? "#CCFF00" : "#C4C8CE" }}>
              {gLabel(g)}
            </button>
          );
        })}
        {editGrade
          ? <>
              <button data-testid={`quickselect-all-${course.id}`} onClick={() => bulk(editGrade, true)}
                className="px-2.5 py-0.5 rounded-full font-display font-bold border bg-[#CCFF00] text-[#000000] border-[#CCFF00] hover:brightness-95">
                Select all {gLabel(editGrade)}
              </button>
              <button data-testid={`quickselect-clear-${course.id}`} onClick={() => bulk(editGrade, false)}
                className="px-2.5 py-0.5 rounded-full font-display font-bold border bg-white text-[#000000] border-[#C4C8CE] hover:bg-[#F4F4F5]">
                Clear all {gLabel(editGrade)}
              </button>
              <span className="text-[#3F6212]">or click lessons one by one · <span className="text-[#000000]">lime outline = on, greyed = off</span></span>
            </>
          : <span className="text-[#000000]">Pick a grade, then click lessons (or "Select all") to set them for that grade.</span>}
      </div>

      <div className="overflow-x-auto p-2">
        <table className="w-full border-collapse" data-testid={`grade-matrix-table-${course.id}`}>
          <thead>
            <tr>
              <th className="w-9 p-2 text-[10px] uppercase tracking-[0.12em] text-[#000000]">#</th>
              {columns.map(col => (
                <th key={col.key} className="text-left p-2 min-w-[164px]">
                  <span className="chip text-[10px] font-bold" style={pillarChipStyle(col.color)}>{col.name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: maxRows }).map((_, r) => (
              <tr key={r} className="border-t border-[#E4E4E7]">
                <td className="p-2 align-middle text-center font-display font-bold text-[#000000]">{r + 1}</td>
                {columns.map(col => {
                  const l = groups[col.key][r];
                  return (
                    <td key={col.key} className="p-2 align-top">
                      {l && (
                        <MatrixLessonCard
                          lesson={l}
                          grades={grid[l.lesson_asset_id]}
                          locked={!!locks[l.lesson_asset_id]}
                          editGrade={editGrade}
                          onToggle={toggle}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PublicLessonCard({ lesson, locked, onLock }) {
  return (
    <div
      className="rounded-xl p-2.5 flex flex-col gap-2 transition-all w-44 max-w-full"
      style={locked
        ? { border: "1px solid #0A0D14", background: "#FFFFFF" }
        : { border: "1px solid #C4C8CE", background: "#FFFFFF" }}
      data-testid={`public-lesson-${lesson.lesson_asset_id}`}
      data-locked={locked ? "true" : "false"}
    >
      <div className="text-sm font-semibold text-[#000000] break-words leading-snug line-clamp-2 min-h-[2.75em]" title={lesson.title}>
        {lesson.title}
      </div>
      <button type="button" data-testid={`public-lock-${lesson.lesson_asset_id}`}
        onClick={() => onLock(lesson.lesson_asset_id, !locked)}
        className="text-[11px] font-display font-bold px-2 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors self-start"
        style={{
          background: locked ? "#0A0D14" : "#FFFFFF",
          color: locked ? "#CCFF00" : "#52525B",
          border: "1px solid " + (locked ? "#0A0D14" : "#C4C8CE"),
        }}
        title={locked ? "Unlock: allow this lesson to be enabled for school grades again" : "Lock: hide from ALL school grades — public learners only"}>
        {locked ? "🔒 Public only" : "🔓 Shared"}
      </button>
    </div>
  );
}

function MatrixLessonCard({ lesson, grades, locked, editGrade, onToggle }) {
  const editActive = !!editGrade && !locked;
  const editOn = editActive && grades.has(editGrade);
  const cardStyle = locked
    ? { border: "1px solid #A1A1AA", background: "#E4E4E7", opacity: 0.55, cursor: "not-allowed" }
    : editActive
    ? (editOn
        ? { cursor: "pointer", border: "2px solid #CCFF00", background: "#FFFFFF", boxShadow: "0 0 0 1px #CCFF00" }
        : { cursor: "pointer", border: "2px solid #C4C8CE", background: "#ECEDEF", opacity: 0.45 })
    : { border: "1px solid #C4C8CE", background: "#FFFFFF" };
  return (
    <div
      onClick={editActive ? () => onToggle(lesson.lesson_asset_id, editGrade) : undefined}
      className="rounded-xl p-2.5 flex flex-col gap-2 transition-all w-44 max-w-full"
      style={cardStyle}
      data-testid={`matrix-lesson-${lesson.lesson_asset_id}`}
      data-locked={locked ? "true" : "false"}
      data-edit-state={editActive ? (editOn ? "on" : "off") : "none"}
      title={locked ? "🔒 Locked to public-only — unlock in the Public (Adults) tab" : (editActive ? `Click to turn ${editGrade} ${editOn ? "OFF" : "ON"} for this lesson` : undefined)}
    >
      <div className="text-sm font-semibold text-[#000000] break-words leading-snug line-clamp-2 min-h-[2.75em]" title={lesson.title}>
        {locked && <span className="mr-1">🔒</span>}{lesson.title}
      </div>
      {locked ? (
        <div className="text-[10px] font-bold uppercase tracking-wide text-[#000000]">Public only — locked</div>
      ) : (
      <div className="grid grid-cols-7 gap-0.5">
        {GRADES.map(g => {
          const on = grades.has(g);
          const isEditG = editActive && g === editGrade;
          return (
            <button key={g} type="button" data-testid={`grade-chip-${lesson.lesson_asset_id}-${g}`} data-state={on ? "on" : "off"}
              onClick={(e) => { e.stopPropagation(); onToggle(lesson.lesson_asset_id, g); }}
              className="py-0.5 rounded text-[11px] font-display font-bold tracking-tighter text-center leading-none transition-colors"
              style={{ background: on ? "#CCFF00" : "#ECEDEF", color: on ? "#1F2937" : "#52525B", border: (isEditG ? "2px solid #00B8D4" : "1px solid " + (on ? "#CCFF00" : "#C4C8CE")), cursor: "pointer", fontSize: g === "DEMO" ? 9 : undefined }}>
              {gLabel(g)}
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
}

function MatrixAudit({ entries }) {
  const [sending, setSending] = useState(false);
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/matrix-audit/digest/send-now");
      data.sent ? toast.success(`Digest emailed (${data.change_count} changes)`) : toast.error(`Not sent: ${data.reason}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };
  return (
    <div className="card" data-testid="matrix-audit-log">
      <div className="font-display font-bold flex items-center gap-2 mb-2">
        <ClockCounterClockwise size={16} weight="duotone" /> Matrix change log
        <span className="text-xs text-[#000000] font-sans font-normal">· who changed which grade's lessons, and when · emailed to you weekly</span>
        <button data-testid="matrix-digest-send-now" className="btn-secondary text-xs ml-auto" style={{padding:"3px 12px"}} onClick={sendNow} disabled={sending}>
          {sending ? "Sending…" : "Email digest now"}
        </button>
      </div>
      {!entries?.length && <div className="text-sm text-[#000000] italic">No matrix changes recorded yet.</div>}
      <div className="flex flex-col gap-2">
        {(entries || []).map(e => (
          <div key={e.id} className="rounded-xl border border-[#E4E4E7] px-3 py-2 text-sm" data-testid={`audit-entry-${e.id}`}>
            <div className="flex items-center gap-2 flex-wrap text-xs text-[#000000]">
              <span className="font-bold text-[#000000]">{e.actor_name}</span>
              <span className="uppercase tracking-wide">{roleLabel(e.actor_role)}</span>
              <span>· {e.course_title}</span>
              <span className="ml-auto">{new Date(e.at).toLocaleString()}</span>
            </div>
            <div className="mt-1 flex flex-col gap-0.5">
              {(e.changes || []).map((ch, i) => (
                <div key={i} className="text-xs">
                  <span className="font-semibold">{ch.title}</span>{": "}
                  {ch.added?.map(g => <span key={g} className="text-[#3F6212] font-bold mr-1">+{g}</span>)}
                  {ch.removed?.map(g => <span key={g} className="text-[#B91C1C] font-bold mr-1">−{g}</span>)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
